import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAvailableSelections } from '@/core/hooks/useAvailableSelections';
import { useSelectorItems } from '@/core/hooks/useSelectorItems';
import { useDefaultAllSelection } from '@/features/global-filter/hooks/useDefaultAllSelection';
import {
  type Sentiment,
  type NormalizedSentiment,
  SENTIMENT_LABEL_TO_NORMALIZED,
  NORMALIZED_SENTIMENT_TO_LABEL,
  SENTIMENT_OPTIONS,
} from '@/core/constants/domainMappings';
import { FilterSelector, type FilterSelectorOption } from '@/core/components/FilterSelector/FilterSelector';
import { SentimentSelector } from './SentimentSelector';
import { TagsIntersectionSwitch } from './TagsIntersectionSwitch';
import { type SelectionMap } from '@/core/components/FilterSelector/types';
import { useToast } from '@/core/components/ToastProvider/ToastProvider';
import { SelectionSummary } from '@/core/components/SelectionSummary';
import './ClassificationContent.scss';

export type { NormalizedSentiment } from '@/core/constants/domainMappings';

export interface ClassificationData {
  groups: SelectionMap;
  themes: SelectionMap;
  tags: SelectionMap;
  classifiedBy: SelectionMap;
  selection: SelectionMap;
  selectionEncodedIds: Record<string, string>;
  status: SelectionMap;
  sentiments: Set<NormalizedSentiment>;
  groupsTouched: boolean;
  themesTouched: boolean;
  sentimentsTouched: boolean;
  classifiedByTouched: boolean;
  tagsIntersection: boolean;
}

export const defaultClassificationData: ClassificationData = {
  groups: {},
  themes: {},
  tags: {},
  classifiedBy: {},
  selection: {},
  selectionEncodedIds: {},
  status: {},
  sentiments: new Set<NormalizedSentiment>(),
  groupsTouched: false,
  themesTouched: false,
  sentimentsTouched: false,
  classifiedByTouched: false,
  tagsIntersection: false,
};


interface ClassificationContentProps {
  onCountChange?: (count: number) => void;
  onChange: (data: ClassificationData) => void;
  values: ClassificationData;
  /**
   * Items available for "Classified by" selection.
   * Accepts plain strings (key = label) or option objects with separate key (ID) and label (display name).
   * When open sea is active this receives universeEditors; when proprietary channels is active it receives repliers.
   */
  classifiedByItems?: Array<string | FilterSelectorOption>;
  disableGroups?: boolean;
}

const MAX_TAGS_INTERSECTION = 20;

function trimTagsToIntersectionLimit(prev: SelectionMap): SelectionMap {
  const addEntries = Object.entries(prev).filter(([, v]) => v === 'add');
  if (addEntries.length <= MAX_TAGS_INTERSECTION) return prev;
  const removeEntries = Object.entries(prev).filter(([, v]) => v === 'remove');
  return {
    ...Object.fromEntries(addEntries.slice(0, MAX_TAGS_INTERSECTION)),
    ...Object.fromEntries(removeEntries),
  };
}

export const ClassificationContent: React.FC<ClassificationContentProps> = ({ 
  onCountChange, 
  onChange, 
  values, 
  classifiedByItems = [], 
  disableGroups = false 
}) => {
  const { t } = useTranslation();
  const { showToast } = useToast();

  const {
    groups: groupsSelection,
    themes: themesSelection,
    tags: tagsSelection,
    classifiedBy: classifiedBySelection,
    selection: selectionSelection,
    status: statusSelection,
    sentiments,
    groupsTouched,
    themesTouched,
    sentimentsTouched,
    classifiedByTouched,
    tagsIntersection,
  } = values;

  // Convert stored sentiments (NormalizedSentiment) to display sentiments (Sentiment)
  const sentimentSelection = useMemo<Set<Sentiment>>(() => {
    if (!sentiments?.size) return new Set<Sentiment>();
    return new Set<Sentiment>(
      [...sentiments]
        .map((s) => NORMALIZED_SENTIMENT_TO_LABEL[s])
        .filter((s): s is Sentiment => !!s)
    );
  }, [sentiments]);

  // Convert stored status keys to display labels
  const statusSelectionDisplay = useMemo<SelectionMap>(() => {
    if (!statusSelection || Object.keys(statusSelection).length === 0) return {};
    const keyToLabel: Record<string, string> = {
      annotated: t('globalFilter.statusOptions.annotated', 'Publicações anotadas'),
      notAnnotated: t('globalFilter.statusOptions.notAnnotated', 'Publicações não anotadas'),
      removed: t('globalFilter.statusOptions.removed', 'Publicações removidas'),
      checked: t('globalFilter.statusOptions.checked', 'Publicações checadas'),
      notChecked: t('globalFilter.statusOptions.notChecked', 'Publicações não checadas'),
    };
    return Object.fromEntries(
      Object.entries(statusSelection).map(([key, action]) => [keyToLabel[key] ?? key, action])
    );
  }, [statusSelection, t]);

  const { effectiveSelection: effectiveClassifiedBySelection, handleChange: handleClassifiedByChange } = useDefaultAllSelection({
    items: classifiedByItems,
    selection: classifiedBySelection,
    touched: classifiedByTouched,
    onCommit: (nextSelection) => onChange({ ...values, classifiedBy: nextSelection, classifiedByTouched: true }),
    getKey: (item) => (typeof item === 'string' ? item : item.key),
  });

  const allSentimentsSet = useMemo(() => new Set<Sentiment>(SENTIMENT_OPTIONS), []);
  
  const effectiveSentimentSelection = useMemo<Set<Sentiment>>(
    () => (!sentimentsTouched && sentimentSelection.size === 0) ? allSentimentsSet : sentimentSelection,
    [sentimentsTouched, sentimentSelection, allSentimentsSet]
  );
  
  const handleSentimentChange = useCallback((updater: React.SetStateAction<Set<Sentiment>>) => {
    const base = (!sentimentsTouched && sentimentSelection.size === 0) ? allSentimentsSet : sentimentSelection;
    const nextSelection = typeof updater === 'function' ? updater(base) : updater;
    
    const normalizedSentiments = new Set<NormalizedSentiment>(
      [...nextSelection].map(s => SENTIMENT_LABEL_TO_NORMALIZED[s] as NormalizedSentiment)
    );
    
    onChange({ ...values, sentiments: normalizedSentiments, sentimentsTouched: true });
  }, [sentimentsTouched, sentimentSelection, allSentimentsSet, onChange, values]);

  const [availableSelections] = useAvailableSelections();
  const selectionNames = useMemo(() => availableSelections.map((s) => s.name), [availableSelections]);
  const { groups: availableGroups, themes: availableThemes, tags: availableTags } = useSelectorItems();

  const { effectiveSelection: effectiveGroupsSelection, handleChange: handleGroupsChange } = useDefaultAllSelection({
    items: availableGroups,
    selection: groupsSelection,
    touched: groupsTouched,
    onCommit: (nextSelection) => onChange({ ...values, groups: nextSelection, groupsTouched: true }),
  });

  const { effectiveSelection: effectiveThemesSelection, handleChange: handleThemesChange } = useDefaultAllSelection({
    items: availableThemes,
    selection: themesSelection,
    touched: themesTouched,
    onCommit: (nextSelection) => onChange({ ...values, themes: nextSelection, themesTouched: true }),
  });

  const handleTagsChangeWithIntersectionLimit = useCallback((updater: React.SetStateAction<SelectionMap>) => {
    const next = typeof updater === 'function' ? updater(tagsSelection) : updater;
    const prevAddCount = Object.values(tagsSelection).filter((v) => v === 'add').length;
    const nextAddCount = Object.values(next).filter((v) => v === 'add').length;

    // Detect bulk action ("Select All"): usually adds multiple tags at once.
    const addedCount = nextAddCount - prevAddCount;
    const isBulkSelect = addedCount > 1;

    if (nextAddCount > MAX_TAGS_INTERSECTION) {
      // Rule: Bulk selection ("Select All") ALWAYS limits to 20 tags, BUT ONLY IF tagsIntersection is ON.
      // If tagsIntersection is OFF, the user can select all tags regardless of the count.
      if (tagsIntersection) {
        if (isBulkSelect) {
          void showToast({
            variant: 'warning',
            title: t('globalFilter.tagsIntersectionLimitTitle', 'Limite de tags'),
            text: t('globalFilter.tagsIntersectionLimitText', 'Você só pode selecionar até 20 tags, com todas as condições'),
            icon: 'warning',
          });
          onChange({ ...values, tags: trimTagsToIntersectionLimit(next) });
          return;
        }

        if (nextAddCount > prevAddCount) {
          void showToast({
            variant: 'warning',
            title: t('globalFilter.tagsIntersectionLimitTitle', 'Limite de tags'),
            text: t('globalFilter.tagsIntersectionLimitText', 'Você só pode selecionar até 20 tags, com todas as condições'),
            icon: 'warning',
          });
          return;
        }
      }
    }
    onChange({ ...values, tags: next });
  }, [tagsSelection, tagsIntersection, showToast, t, onChange, values]);

  const handleTagsIntersectionChange = useCallback((checked: boolean) => {
    if (checked) {
      const addCount = Object.values(tagsSelection).filter((v) => v === 'add').length;
      
      if (addCount > MAX_TAGS_INTERSECTION) {
        void showToast({
          variant: 'warning',
          title: t('globalFilter.tagsIntersectionLimitTitle', 'Limite de tags'),
          text: t('globalFilter.tagsIntersectionLimitText', 'Você só pode selecionar até 20 tags, com todas as condições'),
          icon: 'warning',
        });
        onChange({ ...values, tags: trimTagsToIntersectionLimit(tagsSelection), tagsIntersection: checked });
        return;
      }
    }
    onChange({ ...values, tagsIntersection: checked });
  }, [tagsSelection, showToast, t, onChange, values]);

  const hasSelectedTags = useMemo(() => {
    return Object.keys(tagsSelection).length > 0;
  }, [tagsSelection]);

  const effectiveTagsIntersection = useMemo(() => {
    return hasSelectedTags ? tagsIntersection : false;
  }, [hasSelectedTags, tagsIntersection]);

  // Automatically turn off tagsIntersection when no tags are selected
  useEffect(() => {
    if (!hasSelectedTags && tagsIntersection) {
      onChange({ ...values, tagsIntersection: false });
    }
  }, [hasSelectedTags, tagsIntersection, onChange, values]);

  const handleSelectionChange = useCallback((updater: React.SetStateAction<SelectionMap>) => {
    const nextSelection = typeof updater === 'function' ? updater(selectionSelection) : updater;
    
    // Update selectionEncodedIds when selection changes
    const newEncodedIds: Record<string, string> = {};
    Object.keys(nextSelection).forEach(name => {
      const selection = availableSelections.find(s => s.name === name);
      if (selection) {
        newEncodedIds[name] = selection.encoded_ids;
      }
    });
    
    onChange({ ...values, selection: nextSelection, selectionEncodedIds: newEncodedIds });
  }, [selectionSelection, availableSelections, onChange, values]);

  const handleStatusChange = useCallback((updater: React.SetStateAction<SelectionMap>) => {
    const nextSelectionDisplay = typeof updater === 'function' ? updater(statusSelectionDisplay) : updater;
    
    // Convert display labels back to keys
    const statusLabelToKey: Record<string, string> = {
      [t('globalFilter.statusOptions.annotated', 'Publicações anotadas')]: 'annotated',
      [t('globalFilter.statusOptions.notAnnotated', 'Publicações não anotadas')]: 'notAnnotated',
      [t('globalFilter.statusOptions.removed', 'Publicações removidas')]: 'removed',
      [t('globalFilter.statusOptions.checked', 'Publicações checadas')]: 'checked',
      [t('globalFilter.statusOptions.notChecked', 'Publicações não checadas')]: 'notChecked',
    };
    
    const normalizedStatus: SelectionMap = Object.fromEntries(
      Object.entries(nextSelectionDisplay).map(([label, state]) => [statusLabelToKey[label] || label, state])
    );
    
    onChange({ ...values, status: normalizedStatus });
  }, [statusSelectionDisplay, t, onChange, values]);

  const statusEntries = useMemo(() => [
    { key: 'annotated',    label: t('globalFilter.statusOptions.annotated',    'Publicações anotadas') },
    { key: 'notAnnotated', label: t('globalFilter.statusOptions.notAnnotated', 'Publicações não anotadas') },
    { key: 'removed',      label: t('globalFilter.statusOptions.removed',      'Publicações removidas') },
    { key: 'checked',      label: t('globalFilter.statusOptions.checked',      'Publicações checadas') },
    { key: 'notChecked',   label: t('globalFilter.statusOptions.notChecked',   'Publicações não checadas') },
  ], [t]);

  const classificationStatusOptions = useMemo(() => statusEntries.map(e => e.label), [statusEntries]);

  useEffect(() => {
    if (!onCountChange) return;
    const isFiltered = (sel: SelectionMap, available: string[]) =>
      Object.keys(sel).length > 0 &&
      (Object.keys(sel).length < available.length || Object.values(sel).some((a) => a !== 'add'));
    const count =
      (isFiltered(effectiveGroupsSelection, availableGroups) ? 1 : 0) +
      (isFiltered(effectiveThemesSelection, availableThemes) ? 1 : 0) +
      (Object.keys(tagsSelection).length > 0 ? 1 : 0) +
      (tagsIntersection ? 1 : 0) +
      (Object.keys(effectiveClassifiedBySelection).length > 0 && Object.keys(effectiveClassifiedBySelection).length < classifiedByItems.length ? 1 : 0) +
      (Object.keys(selectionSelection).length > 0 ? 1 : 0) +
      (Object.keys(statusSelection).length > 0 ? 1 : 0) +
      (effectiveSentimentSelection.size < SENTIMENT_OPTIONS.length ? 1 : 0);
    onCountChange(count);
  }, [effectiveGroupsSelection, availableGroups, effectiveThemesSelection, availableThemes, tagsSelection, tagsIntersection, classifiedByItems.length, effectiveClassifiedBySelection, selectionSelection, statusSelection, effectiveSentimentSelection, onCountChange]);

  const groupsPlaceholder = useMemo(() => {
    const hasSelection = Object.values(effectiveGroupsSelection).includes('add');
    return hasSelection
      ? t('globalFilter.noGroups', 'Selecione grupos')
      : t('globalFilter.unclassifiedGroups', 'Não classificados');
  }, [effectiveGroupsSelection, t]);

  const themesPlaceholder = useMemo(() => {
    const hasSelection = Object.values(effectiveThemesSelection).includes('add');
    return hasSelection
      ? t('globalFilter.noThemes', 'Selecione temas')
      : t('globalFilter.unclassifiedThemes', 'Não classificados');
  }, [effectiveThemesSelection, t]);

  return (
    <div className="classification-content">
      <div className={`classification-content__group${disableGroups ? ' classification-content__group--disabled' : ''}`}>
        <FilterSelector
          label={t('classificationCard.groups', 'Grupos')}
          placeholder={groupsPlaceholder}
          availableItems={availableGroups}
          selection={effectiveGroupsSelection}
          onChange={handleGroupsChange}
          tooltipText={t('globalFilter.groupTooltip')}
          disabled={disableGroups}
        />
        <SelectionSummary selection={groupsSelection} availableItemsCount={availableGroups.length} />
      </div>
      <div>
        <FilterSelector
          label={t('classificationCard.themes', 'Temas')}
          placeholder={themesPlaceholder}
          availableItems={availableThemes}
          selection={effectiveThemesSelection}
          onChange={handleThemesChange}
          tooltipText={t('globalFilter.themeTooltip')}
        />
        <SelectionSummary selection={themesSelection} availableItemsCount={availableThemes.length} />
      </div>
      <div>
        <FilterSelector
          label={t('classificationCard.tags', 'Tags')}
          placeholder={t('globalFilter.noTags', 'Selecione tags')}
          availableItems={availableTags}
          selection={tagsSelection}
          onChange={handleTagsChangeWithIntersectionLimit}
          tooltipText={t('globalFilter.tagTooltip')}
          hideSelectAllWhenSingleItem={true}
        />
        <SelectionSummary selection={tagsSelection} availableItemsCount={availableTags.length} />
      </div>
      <TagsIntersectionSwitch
        checked={effectiveTagsIntersection}
        onChange={handleTagsIntersectionChange}
        disabled={!hasSelectedTags}
      />
      <SentimentSelector
        selection={effectiveSentimentSelection}
        onChange={handleSentimentChange}
      />
      <FilterSelector
        label={t('filterBar.selection', 'Arquivos')}
        placeholder={t('globalFilter.noSelection', 'Selecione arquivos')}
        availableItems={selectionNames}
        selection={selectionSelection}
        onChange={handleSelectionChange}
        allowNegation={false}
        singleSelect
      />
      <div>
        <FilterSelector
          label={t('filterBar.classifiedBy', 'Classificado por')}
          placeholder={t('globalFilter.noClassifiedBy', 'Selecione usuários')}
          availableItems={classifiedByItems}
          selection={effectiveClassifiedBySelection}
          onChange={handleClassifiedByChange}
          allowNegation={false}
        />
        <SelectionSummary selection={classifiedBySelection} availableItemsCount={classifiedByItems.length} />
      </div>
      <div>
        <FilterSelector
          label={t('globalFilter.classificationStatus', 'Status da classificação')}
          placeholder={t('globalFilter.noStatus', 'Selecione status')}
          availableItems={classificationStatusOptions}
          selection={statusSelectionDisplay}
          onChange={handleStatusChange}
          allowNegation={false}
        />
        <SelectionSummary selection={statusSelection} availableItemsCount={classificationStatusOptions.length} />
      </div>
    </div>
  );
};
